import { useEffect } from 'react';
import { Controller, useForm, useWatch } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import toast from 'react-hot-toast';
import type { MembershipAddon } from '@court-sync/shared';
import {
  membershipAddonDrawerFormSchema,
  membershipAddonFormToCreatePayload,
  membershipAddonFormToUpdatePayload,
  type MembershipAddonDrawerFormValues,
} from '@court-sync/shared';
import {
  Button,
  Drawer,
  Field,
  FieldError,
  FieldLabel,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Switch,
  Textarea,
} from '@/components/ui';
import { clubMembersService } from '@/services/clubMembers';

const ADDON_FORM_ID = 'membership-addon-form';

interface MembershipAddonDrawerProps {
  clubId: string;
  currency: string;
  addon: MembershipAddon | null;
  isOpen: boolean;
  onClose: () => void;
  onSaved: () => void;
}

export function toFormValues(addon: MembershipAddon | null): MembershipAddonDrawerFormValues {
  if (!addon) {
    return {
      name: '',
      description: '',
      price: 0,
      hasConcessionPrice: false,
      concessionPrice: null,
      pricingMode: 'per_adult',
      isActive: true,
      playEventBenefit: 'none',
      playEventDiscountPercent: null,
    };
  }

  return {
    name: addon.name,
    description: addon.description ?? '',
    price: addon.priceCents / 100,
    hasConcessionPrice: addon.concessionPriceCents != null,
    concessionPrice: addon.concessionPriceCents != null ? addon.concessionPriceCents / 100 : null,
    pricingMode: addon.pricingMode,
    isActive: addon.isActive,
    playEventBenefit: addon.playEventBenefit ?? 'none',
    playEventDiscountPercent: addon.playEventDiscountPercent ?? null,
  };
}

export function resolveAddonCurrency(addon: MembershipAddon | null, createCurrency: string): string {
  return addon?.currency ?? createCurrency;
}

export function MembershipAddonDrawer({
  clubId,
  currency: createCurrency,
  addon,
  isOpen,
  onClose,
  onSaved,
}: MembershipAddonDrawerProps) {
  const isEditing = addon != null;
  const effectiveCurrency = resolveAddonCurrency(addon, createCurrency);

  const {
    control,
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<MembershipAddonDrawerFormValues>({
    resolver: zodResolver(membershipAddonDrawerFormSchema),
    defaultValues: toFormValues(addon),
  });

  const hasConcessionPrice = useWatch({ control, name: 'hasConcessionPrice' });
  const playEventBenefit = useWatch({ control, name: 'playEventBenefit' });

  useEffect(() => {
    if (isOpen) {
      reset(toFormValues(addon));
    }
  }, [isOpen, addon, reset]);

  const onSubmit = async (values: MembershipAddonDrawerFormValues) => {
    const payload = isEditing
      ? membershipAddonFormToUpdatePayload(values, effectiveCurrency)
      : membershipAddonFormToCreatePayload(values, effectiveCurrency);

    const res = isEditing
      ? await clubMembersService.updateMembershipAddon(clubId, addon.id, payload)
      : await clubMembersService.createMembershipAddon(clubId, payload);

    if (res.success) {
      toast.success(isEditing ? 'Add-on updated' : 'Add-on created');
      onSaved();
      onClose();
    } else {
      toast.error(res.error || (isEditing ? 'Failed to update add-on' : 'Failed to create add-on'));
    }
  };

  return (
    <Drawer
      isOpen={isOpen}
      onClose={onClose}
      title={isEditing ? 'Edit add-on' : 'Create add-on'}
      description="Stackable extras members can add to a membership, e.g. Gold Extra or Night Extra."
      width="md"
      footer={
        <div className="flex justify-end gap-2">
          <Button type="button" variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" form={ADDON_FORM_ID} isLoading={isSubmitting}>
            {isEditing ? 'Save add-on' : 'Create add-on'}
          </Button>
        </div>
      }
    >
      <form id={ADDON_FORM_ID} onSubmit={handleSubmit(onSubmit)} className="space-y-5">
        <Field data-invalid={!!errors.name?.message}>
          <FieldLabel htmlFor="membership-addon-name">Name</FieldLabel>
          <Input
            id="membership-addon-name"
            placeholder="e.g. Gold Extra"
            aria-invalid={!!errors.name?.message}
            {...register('name')}
          />
          <FieldError>{errors.name?.message}</FieldError>
        </Field>

        <Field>
          <FieldLabel htmlFor="membership-addon-description">Description</FieldLabel>
          <Textarea
            id="membership-addon-description"
            rows={3}
            placeholder="Optional benefit summary shown to members"
            {...register('description')}
          />
        </Field>

        <Field data-invalid={!!errors.pricingMode?.message}>
          <FieldLabel htmlFor="membership-addon-pricing-mode">Pricing</FieldLabel>
          <Controller
            name="pricingMode"
            control={control}
            render={({ field }) => (
              <Select value={field.value} onValueChange={field.onChange}>
                <SelectTrigger id="membership-addon-pricing-mode">
                  <SelectValue placeholder="Select pricing mode" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="per_adult">Per adult (stacks with adult seats)</SelectItem>
                  <SelectItem value="per_membership">Per membership (flat, once)</SelectItem>
                </SelectContent>
              </Select>
            )}
          />
          <FieldError>{errors.pricingMode?.message}</FieldError>
        </Field>

        <Field data-invalid={!!errors.price?.message}>
          <FieldLabel htmlFor="membership-addon-price">Price ({effectiveCurrency})</FieldLabel>
          <Input
            id="membership-addon-price"
            type="number"
            min={0}
            step={0.01}
            placeholder="0.00"
            aria-invalid={!!errors.price?.message}
            {...register('price')}
          />
          {isEditing ? (
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
              Currency: {effectiveCurrency} (fixed for this add-on)
            </p>
          ) : null}
          <FieldError>{errors.price?.message}</FieldError>
        </Field>

        <div className="flex items-start gap-3">
          <Controller
            name="hasConcessionPrice"
            control={control}
            render={({ field }) => (
              <Switch
                id="membership-addon-has-concession"
                checked={field.value}
                onCheckedChange={field.onChange}
              />
            )}
          />
          <label htmlFor="membership-addon-has-concession" className="cursor-pointer text-sm">
            <span className="font-medium text-gray-900 dark:text-white">Discounted concession price</span>
            <p className="mt-1 text-gray-600 dark:text-gray-400">
              Charge a lower per-adult rate when the covered adult holds a concession seat.
            </p>
          </label>
        </div>

        {hasConcessionPrice ? (
          <Field data-invalid={!!errors.concessionPrice?.message}>
            <FieldLabel htmlFor="membership-addon-concession-price">
              Concession price ({effectiveCurrency})
            </FieldLabel>
            <Input
              id="membership-addon-concession-price"
              type="number"
              min={0}
              step={0.01}
              placeholder="0.00"
              aria-invalid={!!errors.concessionPrice?.message}
              {...register('concessionPrice', {
                setValueAs: (value: string) => (value === '' ? null : Number(value)),
              })}
            />
            <FieldError>{errors.concessionPrice?.message}</FieldError>
          </Field>
        ) : null}

        <Field data-invalid={!!errors.playEventBenefit?.message}>
          <FieldLabel htmlFor="membership-addon-play-benefit">Play event benefit</FieldLabel>
          <Controller
            name="playEventBenefit"
            control={control}
            render={({ field }) => (
              <Select value={field.value} onValueChange={field.onChange}>
                <SelectTrigger id="membership-addon-play-benefit">
                  <SelectValue placeholder="No play benefit" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">None</SelectItem>
                  <SelectItem value="full_coverage">Full coverage (free play events)</SelectItem>
                  <SelectItem value="discount_percent">Percentage discount on play events</SelectItem>
                </SelectContent>
              </Select>
            )}
          />
          <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
            Optional perk for members with this add-on when joining play events.
          </p>
          <FieldError>{errors.playEventBenefit?.message}</FieldError>
        </Field>

        {playEventBenefit === 'discount_percent' ? (
          <Field data-invalid={!!errors.playEventDiscountPercent?.message}>
            <FieldLabel htmlFor="membership-addon-play-discount">Play discount (%)</FieldLabel>
            <Input
              id="membership-addon-play-discount"
              type="number"
              min={0}
              max={100}
              step={1}
              placeholder="e.g. 50"
              aria-invalid={!!errors.playEventDiscountPercent?.message}
              {...register('playEventDiscountPercent', {
                setValueAs: (value: string) => (value === '' ? null : Number(value)),
              })}
            />
            <FieldError>{errors.playEventDiscountPercent?.message}</FieldError>
          </Field>
        ) : null}

        <div className="flex items-start gap-3">
          <Controller
            name="isActive"
            control={control}
            render={({ field }) => (
              <Switch
                id="membership-addon-active"
                checked={field.value}
                onCheckedChange={field.onChange}
              />
            )}
          />
          <label htmlFor="membership-addon-active" className="cursor-pointer text-sm">
            <span className="font-medium text-gray-900 dark:text-white">Active</span>
            <p className="mt-1 text-gray-600 dark:text-gray-400">
              Inactive add-ons cannot be selected on new memberships.
            </p>
          </label>
        </div>
      </form>
    </Drawer>
  );
}
